import * as Yup from 'yup';
import {ISelectItem} from '~/components/Controls/Select';
import {ID} from '~/lib/model';

/** Keys match `models.sellers.SensitiveCategory` in api-gateway. */
export enum SensitiveCategory {
    REAL_MONEY_GAMBLING = 'real_money_gambling',
    SOCIAL_CASINO = 'social_casino',
    SPORTS_BETTING = 'sports_betting',
    ALCOHOL = 'alcohol',
    TOBACCO = 'tobacco',
    CANNABIS = 'cannabis',
    PHARMACEUTICALS = 'pharmaceuticals',
    SUPPLEMENTS = 'supplements',
    DATING_ADULT = 'dating_adult',
    POLITICAL = 'political',
    RELIGIOUS = 'religious',
    FIREARMS = 'firearms',
    CRYPTO = 'crypto',
    SHORT_TERM_CREDIT = 'short_term_credit',
    SUBSCRIPTION_TRAP = 'subscription_trap',
    SWEEPSTAKES = 'sweepstakes'
}

export const sensitiveCategories: ISelectItem<SensitiveCategory>[] = [
    {value: SensitiveCategory.REAL_MONEY_GAMBLING, label: 'Real-money gambling & casino'},
    {value: SensitiveCategory.SOCIAL_CASINO, label: 'Social casino (no real money)'},
    {value: SensitiveCategory.SPORTS_BETTING, label: 'Sports betting'},
    {value: SensitiveCategory.ALCOHOL, label: 'Alcohol & spirits'},
    {value: SensitiveCategory.TOBACCO, label: 'Tobacco & e-cigarettes / vaping'},
    {value: SensitiveCategory.CANNABIS, label: 'Cannabis (where legal)'},
    {value: SensitiveCategory.PHARMACEUTICALS, label: 'Pharmaceuticals / OTC drugs'},
    {value: SensitiveCategory.SUPPLEMENTS, label: 'Dietary supplements & weight loss'},
    {value: SensitiveCategory.DATING_ADULT, label: 'Dating & adult content'},
    {value: SensitiveCategory.POLITICAL, label: 'Political advertising'},
    {value: SensitiveCategory.RELIGIOUS, label: 'Religious / faith content'},
    {value: SensitiveCategory.FIREARMS, label: 'Firearms & weapons accessories'},
    {value: SensitiveCategory.CRYPTO, label: 'Cryptocurrency & NFTs'},
    {value: SensitiveCategory.SHORT_TERM_CREDIT, label: 'Buy-now-pay-later / payday loans'},
    {value: SensitiveCategory.SUBSCRIPTION_TRAP, label: 'Subscription trap / negative option billing'},
    {value: SensitiveCategory.SWEEPSTAKES, label: 'Sweepstakes & prize promotions'}
];

export const getSensitiveCategoryLabel = (category: SensitiveCategory): string =>
    sensitiveCategories.find(item => item.value === category)?.label ?? category;

export enum SensitiveCategoryDecision {
    ALLOWED = 'allowed',
    NOT_ALLOWED = 'not_allowed'
}

export const sensitiveCategoryDecisions: ISelectItem<SensitiveCategoryDecision>[] = [
    {value: SensitiveCategoryDecision.ALLOWED, label: 'Allowed'},
    {value: SensitiveCategoryDecision.NOT_ALLOWED, label: 'Not allowed'}
];

export const getSensitiveCategoryDecisionLabel = (decision: SensitiveCategoryDecision): string =>
    sensitiveCategoryDecisions.find(item => item.value === decision)?.label ?? decision;

/** A seller's answer for one category. Exception bundles take the opposite decision from the account. */
export interface ISensitiveCategoryDeclaration {
    sellerId: ID;
    category: SensitiveCategory;
    decision: SensitiveCategoryDecision;
    exceptionBundles: string[];
    note?: string | null;
    updatedByRole: string;
    updatedAt: string;
}

export interface ISensitiveCategoryDeclarationRequest {
    decision: SensitiveCategoryDecision;
    exceptionBundles: string[];
    note?: string | null;
}

export interface ISensitiveCategoryReportRow {
    sellerId: ID;
    sellerName?: string | null;
    accountManagerId?: ID | null;
    category: SensitiveCategory;
    decision: SensitiveCategoryDecision;
    exceptionBundles: string[];
    note?: string | null;
    updatedAt: string;
}

const publisherRoles = ['seller', 'selfServe'];

/** Tells a publisher's own answer from an account manager's in the Updated column. */
export const getEditorLabel = (role: string): string => publisherRoles.includes(role) ? 'Publisher' : 'AM';

export interface ISensitiveCategoryFormValues {
    category: SensitiveCategory;
    decision: SensitiveCategoryDecision | null;
    exceptionBundles: string;
    note: string;
}

export const SensitiveCategorySchema = Yup.object().shape<ISensitiveCategoryFormValues>({
    category: Yup.mixed<SensitiveCategory>().required(),
    decision: Yup.mixed<SensitiveCategoryDecision>().required('Choose a decision').nullable(),
    exceptionBundles: Yup.string(),
    note: Yup.string()
});

export const toFormValues = (
    category: SensitiveCategory,
    declaration?: ISensitiveCategoryDeclaration
): ISensitiveCategoryFormValues => ({
    category,
    decision: declaration?.decision ?? null,
    exceptionBundles: (declaration?.exceptionBundles ?? []).join('\n'),
    note: declaration?.note ?? ''
});

/** One bundle per line or comma; blanks are dropped. */
export const parseBundleList = (text: string): string[] =>
    text.split(/[\n,]/).map(s => s.trim()).filter(s => s.length > 0);

/** Builds the save request from validated form values, so a missing decision is a programming error. */
export const toDeclarationRequest = (values: ISensitiveCategoryFormValues): ISensitiveCategoryDeclarationRequest => {
    if (!values.decision) {
        throw new Error('Decision is required');
    }
    return {
        decision: values.decision,
        exceptionBundles: parseBundleList(values.exceptionBundles),
        note: values.note.trim() ? values.note.trim() : null
    };
};
